import { useMemo } from 'react';
import { useLanguage } from '@/lib/i18n';
import { getProductContent } from '@/lib/productContent';
import { getProductType } from '@/lib/productRegistry';

interface TranslatedStep {
  step: number;
  title: string;
  description: string;
}

interface TranslatedIngredient {
  name: string;
  description: string;
  icon: 'sparkles' | 'droplets' | 'zap';
  iconColor: string;
  image?: string;
}

interface TranslatedTimelineItem {
  time: string;
  description: string;
}

interface TranslatedReview {
  name: string;
  rating: number;
  text: string;
  verified: boolean;
  image?: string | null;
  reviewImage?: string | null;
}

interface TranslatedMediaItem {
  type: 'image' | 'gif' | 'video';
  placeholder: boolean;
  caption: string;
}

interface TranslatedFAQItem {
  question: string;
  answer: string;
}

interface TranslatedSpec {
  volume: string;
  skinType: string;
  madeIn: string;
  shelfLife: string;
}

interface TranslatedHighlight {
  text: string;
}

interface TranslatedScienceInsideCard {
  icon: string;
  title: string;
  subtitle: string;
  points: string[];
}

interface TranslatedScienceInside {
  title: string;
  subtitle: string;
  tagline: string;
  cards: TranslatedScienceInsideCard[];
}

interface TranslatedProductContent {
  title: string | null;
  description: string | null;
  howToUse: TranslatedStep[];
  ingredients: TranslatedIngredient[];
  resultsTimeline: TranslatedTimelineItem[];
  reviews: TranslatedReview[];
  mediaGallery: TranslatedMediaItem[];
  faq: TranslatedFAQItem[];
  specs: TranslatedSpec;
  highlights: TranslatedHighlight[];
  scienceInside: TranslatedScienceInside | null;
}

export function useTranslatedProductContent(handle: string): TranslatedProductContent {
  const { t, language } = useLanguage();
  const content = getProductContent(handle);
  const productType = getProductType(handle);

  return useMemo(() => {
    // Try to get translated title/description
    const titleKey = `productContent.title.${productType}`;
    const descKey = `productContent.description.${productType}`;
    const translatedTitle = t(titleKey);
    const translatedDesc = t(descKey);
    
    return {
      // Return null if translation key equals the key itself (no translation found)
      title: translatedTitle !== titleKey ? translatedTitle : null,
      description: translatedDesc !== descKey ? translatedDesc : null,
      howToUse: content.howToUse.map(step => ({
        step: step.step,
        title: t(step.titleKey),
        description: t(step.descriptionKey),
      })),
      ingredients: content.ingredients.map(ing => ({
        name: t(ing.nameKey),
        description: t(ing.descriptionKey),
        icon: ing.icon,
        iconColor: ing.iconColor,
        image: ing.image,
      })),
      resultsTimeline: content.resultsTimeline.map(item => ({
        time: t(item.timeKey),
        description: t(item.descriptionKey),
      })),
      reviews: content.reviews.map(review => ({
        name: t(review.nameKey),
        rating: review.rating,
        text: t(review.textKey),
        verified: review.verified,
        image: review.image || null,
        reviewImage: review.reviewImage || null,
      })),
      mediaGallery: content.mediaGallery.map(item => ({
        type: item.type,
        placeholder: item.placeholder,
        caption: t(item.captionKey),
      })),
      faq: content.faq.map(item => ({
        question: t(item.questionKey),
        answer: t(item.answerKey),
      })),
      specs: {
        volume: t(content.specs.volumeKey),
        skinType: t(content.specs.skinTypeKey),
        madeIn: t(content.specs.madeInKey),
        shelfLife: t(content.specs.shelfLifeKey),
      },
      highlights: content.highlights.map(item => ({
        text: t(item.textKey),
      })),
      scienceInside: content.scienceInside ? {
        title: t(content.scienceInside.titleKey),
        subtitle: t(content.scienceInside.subtitleKey),
        tagline: t(content.scienceInside.taglineKey),
        cards: content.scienceInside.cards.map(card => ({
          icon: card.icon,
          title: t(card.titleKey),
          subtitle: t(card.subtitleKey),
          points: card.points.map(p => t(p)),
        })),
      } : null,
    };
  }, [content, t, productType, language]);
}

export type { TranslatedFAQItem };
